// lib
import React from 'react'
import { Map } from 'immutable'
import PropTypes from 'prop-types'

// resources
import createCharLimitPlugin from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/char-limit-plugin'

// components
import RichText from '@aytm/lib/rfd-ui/components/advanced/rich-text'
import ManagedRenderComponent from '@aytm/lib/common/components/ManagedRenderComponent'

// styles
import '@aytm/lib/rfd-ui/styles/advanced/rich-text-counter.scss'

function signzp(value) {
  return 0 > value ? -1 : 1
}

export const defaultCountText = text => text.trim().replace(/\n/g, '').length

export default class RichTextCounter extends ManagedRenderComponent {
  static propTypes = {
    characters: PropTypes.number,
    trimAt: PropTypes.number,
    alignment: PropTypes.string,
    countText: PropTypes.func,
    readOnly: PropTypes.bool,
    onCount: PropTypes.func,
    onUpdate: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    borders: PropTypes.instanceOf(Map),
    onBorderCrossing: PropTypes.func,
    plugins: PropTypes.array
  }
  static updateOnProps = 'pure data'

  constructor(props) {
    super(props)
    this.onRichTextFocus = this.onRichTextFocus.bind(this);
    this.onRichTextBlur = this.onRichTextBlur.bind(this);
    this.onRichTextUpdate = this.onRichTextUpdate.bind(this);
    this.detectBorderCrossing = this.detectBorderCrossing.bind(this);
    this.recount = this.recount.bind(this);
    this.setPlugins = this.setPlugins.bind(this);
    this.state = {
      counter: props.characters || 0,
      active: false,
      sectionClassName: null
    }
    this.setPlugins(props)
  }

  onRichTextFocus(event) {
    const { active } = this.state
    const { onFocus } = this.props
    if ('function' == typeof onFocus) {
      onFocus(event)
    }
    if (!active) {
      this.setState({ active: true })
    }
  }

  onRichTextBlur(event) {
    const { active } = this.state
    const { onBlur } = this.props
    if ('function' == typeof onBlur) onBlur(event)
    if (active) {
      this.setState({ active: false })
    }
  }

  onRichTextUpdate(editorState, prevEditorState, getContent) {
    const { onUpdate } = this.props
    if ('function' == typeof onUpdate) onUpdate(editorState, prevEditorState, getContent)
    if (editorState.getCurrentContent() != prevEditorState.getCurrentContent()) {
      this.recount(editorState)
    }
  }

  detectBorderCrossing(newCounter, lastCounter) {
    const { borders, onBorderCrossing } = this.props
    if (borders) {
      const { crossedBorder, crossedBorderName, direction } = borders.reduce(
        ({ crossedBorder, crossedBorderName, direction }, border, borderName) => {
          const borderCounter = border.get('counter')
          if (signzp(newCounter - borderCounter) != signzp(lastCounter - borderCounter)) {
            return crossedBorder &&
              Math.abs(crossedBorder.get('counter') - newCounter) < Math.abs(borderCounter - newCounter)
              ? { crossedBorder, crossedBorderName, direction }
              : { crossedBorder: border, crossedBorderName: borderName, direction: signzp(newCounter - borderCounter) }
          }
          return { crossedBorder, crossedBorderName, direction }
        },
        { crossedBorder: null, crossedBorderName: null, direction: null }
      )
      if (crossedBorder && crossedBorderName) {
        if ('function' == typeof onBorderCrossing) {
          onBorderCrossing(crossedBorderName, direction, crossedBorder)
        }
        this.setState({
          sectionClassName:
            0 > direction && crossedBorder.has('pnClassName')
              ? crossedBorder.get('pnClassName')
              : 0 < direction && crossedBorder.has('npClassName')
              ? crossedBorder.get('npClassName')
              : null
        })
      }
    }
  }

  recount(editorState, props = this.props) {
    if (null != this.refRichText && 'function' == typeof this.refRichText.getText) {
      const { characters = 0, countText = defaultCountText, onCount, borders } = props
      const { counter } = this.state
      const text = this.refRichText.getText({ editorState })
      const updatedCounter = characters - countText(text)
      if (updatedCounter != counter) {
        this.setState(
          {
            counter: updatedCounter
          },
          () => {
            if ('function' == typeof onCount) {
              onCount(updatedCounter)
            }
            if (borders) {
              this.detectBorderCrossing(updatedCounter, counter)
            }
          }
        )
      }
    }
  }

  setPlugins(props = this.props) {
    const { characters = 0, trimAt, countText = defaultCountText, plugins } = props
    this.plugins =
      null == trimAt ? plugins : [...(plugins || []), createCharLimitPlugin({ limit: characters - trimAt, countText })]
  }

  componentDidMount() {
    this.recount()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.trimAt != this.props.trimAt ||
      nextProps.characters != this.props.characters ||
      nextProps.plugins != this.props.plugins
    ) {
      this.setPlugins(nextProps)
    }
    if (nextProps.borders != this.props.borders || nextProps.characters != this.props.characters) {
      this.recount(undefined, nextProps)
    }
    if (nextProps.readOnly != this.props.readOnly && nextProps.readOnly) this.setState({ active: false })
  }

  render() {
    const {
      alignment = 'left',
      characters,
      trimAt,
      countText,
      onCount,
      onUpdate,
      onFocus,
      onBlur,
      borders,
      onBorderCrossing,
      plugins: externalPlugins,
      ...otherRichTextProps
    } = this.props
    const { counter, active, sectionClassName } = this.state
    return (
      <div
        className={`rich-text-counter rich-text-counter-${alignment}${active ? ' active' : ''}${
          sectionClassName ? ' ' + sectionClassName : ''
        }${counter < 0 ? ' rich-text-counter-negative' : ''}`}
      >
        {Boolean(characters) && (
          <div className="rich-text-counter-display" key="counter">
            {counter}
          </div>
        )}
        <RichText
          alignment={alignment}
          onUpdate={this.onRichTextUpdate}
          onFocus={this.onRichTextFocus}
          onBlur={this.onRichTextBlur}
          plugins={this.plugins}
          ref={this.saveRef('RichText')}
          key="richtext"
          {...otherRichTextProps}
        />
      </div>
    )
  }
}
